import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { motion, useScroll, useTransform, useInView, animate } from "motion/react";
import { useAuth } from "@/hooks/useAuth";
import { BeamsBackground } from "@/components/ui/beams-background";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Accessibility-friendly palette (red-green color blindness safe)
const GREEN = "#0ea5e9";   // sky-blue → positive / gain / HALAL
const RED = "#f97316";     // orange   → negative / loss / NOT HALAL
const HEADLINE_FONT = "'Sora', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', system-ui, sans-serif";

/* ─── Logo: Arabic calligraphy "حلال" with Islamic geometric accent ─── */
function HalalLogo({ size = 22, className = "" }) {
  const scale = size / 28;
  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      aria-label="Halal Screener logo"
    >
      <svg width={size} height={size} viewBox="0 0 28 28" fill="none" className="absolute inset-0">
        <rect x="4" y="4" width="20" height="20" rx="2" transform="rotate(45 14 14)" stroke="rgb(14,165,233)" strokeWidth="0.6" opacity="0.45" />
      </svg>
      <span
        style={{
          fontFamily: "'Amiri', 'Noto Naskh Arabic', serif",
          fontSize: `${14 * scale}px`,
          lineHeight: 1,
          color: "rgb(14,165,233)",
          fontWeight: 700,
          letterSpacing: "0.02em",
        }}
        className="relative z-10 select-none"
        dir="rtl"
      >
        حلال
      </span>
    </div>
  );
}

/* ─── Navbar ─── */
function Navbar({ onEnterScreener }) {
  const { user, showAuthModal, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navLinks = [
    { label: "MARKETS", action: () => document.getElementById("stats-section")?.scrollIntoView({ behavior: "smooth" }) },
    { label: "SCREENER", action: () => onEnterScreener() },
    { label: "PORTFOLIO", action: () => onEnterScreener("portfolio") },
    { label: "METHOD", action: () => document.getElementById("methodology")?.scrollIntoView({ behavior: "smooth" }) },
    { label: "RESEARCH", action: () => onEnterScreener("calendar") },
  ];

  return (
    <nav className="w-full border-b" style={{ borderColor: "var(--paper-border)", background: "var(--paper-bg)" }} data-testid="landing-navbar">
      <div className="flex items-center justify-between px-5 sm:px-10 py-3">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2.5" data-testid="landing-logo">
          <HalalLogo size={22} />
          <span className="text-[15px] tracking-tight" style={{ color: "var(--paper-fg)" }}>
            <span className="font-bold">Halal</span> <span className="font-medium" style={{ color: "var(--paper-muted)" }}>Screener</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-6">
          {navLinks.map(n => (
            <button key={n.label} onClick={n.action} className="text-[11px] font-medium tracking-[0.12em] hover:opacity-70 transition-opacity" style={{ color: "var(--paper-fg)", fontFamily: BODY_FONT }}>
              {n.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden flex flex-col gap-1 p-1" data-testid="mobile-menu-btn">
            <span className="block w-4 h-[1.5px]" style={{ background: "var(--paper-fg)" }} />
            <span className="block w-4 h-[1.5px]" style={{ background: "var(--paper-fg)" }} />
            <span className="block w-3 h-[1.5px]" style={{ background: "var(--paper-fg)" }} />
          </button>
          {user ? (
            <button onClick={logout} className="text-[11px] hidden sm:block hover:opacity-70 transition-opacity" style={{ color: "var(--paper-muted)", fontFamily: BODY_FONT }}>Sign Out</button>
          ) : (
            <button onClick={() => showAuthModal()} className="text-[11px] hidden sm:block hover:opacity-70 transition-opacity" style={{ color: "var(--paper-muted)", fontFamily: BODY_FONT }} data-testid="landing-signin">Sign In</button>
          )}
          <button
            onClick={() => onEnterScreener()}
            className="text-[11px] font-semibold tracking-wide px-4 py-2 rounded-md transition-transform hover:scale-[1.03]"
            style={{ background: "var(--paper-fg)", color: "var(--paper-bg)", fontFamily: BODY_FONT }}
            data-testid="landing-go-screener"
          >
            START SCREENING
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t" style={{ borderColor: "var(--paper-border)" }}>
          {navLinks.map(n => (
            <button key={n.label} onClick={() => { n.action(); setMobileOpen(false); }} className="block w-full text-left px-5 py-3 text-[11px] font-medium tracking-[0.12em] border-b" style={{ borderColor: "var(--paper-border)", color: "var(--paper-fg)", fontFamily: BODY_FONT }}>
              {n.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

/* ─── Ticker Bar — institutional finance aesthetic ─── */
function TickerBar() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/market/indices`).then(r => { if (r.data?.length) setItems(r.data); }).catch(() => {});
    const i = setInterval(() => { axios.get(`${API}/market/indices`).then(r => { if (r.data?.length) setItems(r.data); }).catch(() => {}); }, 30000);
    return () => clearInterval(i);
  }, []);

  if (!items.length) {
    return <div className="w-full h-8 border-b" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0a0a0a" }} data-testid="market-ticker-bar" />;
  }

  return (
    <div
      className="w-full overflow-hidden border-b"
      style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0a0a0a" }}
      data-testid="market-ticker-bar"
    >
      <div className="flex items-center h-8">
        <div className="flex-1 overflow-hidden">
          <div className="flex items-center ticker-scroll">
            {[...items, ...items].map((it, i) => {
              const up = (it.change ?? 0) >= 0;
              const isLast = i === items.length * 2 - 1;
              return (
                <div
                  key={`${it.name}-${i}`}
                  className="flex items-center gap-2 shrink-0 px-5"
                  style={{
                    borderRight: isLast ? "none" : "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <span
                    className="text-[10px] font-medium uppercase tracking-[0.08em]"
                    style={{ color: "rgba(255,255,255,0.55)", fontFamily: BODY_FONT }}
                  >
                    {it.name}
                  </span>
                  <span
                    className="text-[11px] font-semibold tabular-nums"
                    style={{ color: "#ffffff", fontFamily: BODY_FONT }}
                  >
                    {typeof it.price === "number"
                      ? it.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : "—"}
                  </span>
                  <span
                    className="text-[10px] font-medium tabular-nums"
                    style={{ color: up ? "#60a5fa" : "#fb923c", fontFamily: BODY_FONT }}
                  >
                    {up ? "+" : ""}{it.change?.toFixed(2)}
                  </span>
                  <span
                    className="text-[10px] tabular-nums"
                    style={{ color: up ? "rgba(96, 165, 250, 0.75)" : "rgba(251, 146, 60, 0.75)", fontFamily: BODY_FONT }}
                  >
                    {up ? "+" : ""}{it.change_percent?.toFixed(2)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Hero (Fix 2: search auto-suggestions on focus) ─── */
function Hero({ onEnterScreener, stats, allStocks }) {
  const [searchVal, setSearchVal] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const searchRef = useRef(null);

  const handleSearch = () => {
    const t = searchVal.trim().toUpperCase();
    setShowSuggestions(false);
    t ? onEnterScreener(null, t) : onEnterScreener();
  };

  const handleFocus = () => {
    if (allStocks.length > 0) {
      const initial = searchVal.trim()
        ? allStocks.filter(s => s.ticker.toLowerCase().includes(searchVal.toLowerCase()) || s.company_name?.toLowerCase().includes(searchVal.toLowerCase())).slice(0, 8)
        : allStocks.slice(0, 8);
      setSuggestions(initial);
      setShowSuggestions(true);
    }
  };

  const handleChange = (val) => {
    setSearchVal(val);
    if (allStocks.length > 0) {
      const q = val.trim().toLowerCase();
      const filtered = q
        ? allStocks.filter(s => s.ticker.toLowerCase().includes(q) || s.company_name?.toLowerCase().includes(q)).slice(0, 8)
        : allStocks.slice(0, 8);
      setSuggestions(filtered);
      setShowSuggestions(true);
    }
  };

  const pickSuggestion = (ticker) => {
    setSearchVal(ticker);
    setShowSuggestions(false);
    onEnterScreener(null, ticker);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <section data-testid="hero-section">
      <BeamsBackground intensity="strong" className="px-5 sm:px-10 pt-20 pb-12">
        <h1 className="text-center mb-5" style={{ fontFamily: HEADLINE_FONT }}>
          <motion.span
            className="block text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-[-0.035em]"
            style={{ color: "#fafafa" }}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            Invest without <span className="hero-gradient-text">doubt.</span>
          </motion.span>
        </h1>

        <p className="text-center mb-6" style={{ fontFamily: HEADLINE_FONT }}>
          <motion.span
            className="text-lg sm:text-xl font-light tracking-tight inline-block"
            style={{ color: "rgba(250,250,250,0.62)", letterSpacing: "-0.01em" }}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-subheadline"
          >
            Halal investing, auditable by line item.
          </motion.span>
        </p>

        <motion.p
          className="text-center max-w-lg mx-auto mb-2 text-[13px] leading-relaxed"
          style={{ color: "rgba(250,250,250,0.55)", fontFamily: BODY_FONT }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          A free halal stock screener for investors who want to see the math, not just the verdict. Every ratio links to its source filing. <span className="underline font-medium cursor-pointer hover:text-white transition-colors" style={{ color: "rgba(250,250,250,0.85)" }} onClick={() => document.getElementById("methodology")?.scrollIntoView({ behavior: "smooth" })}>Every verdict shows its work.</span>
        </motion.p>
      </BeamsBackground>

      <div className="px-5 sm:px-10 pt-10 pb-12">
      {/* Search bar with suggestions (Fix 2) */}
      <div className="relative max-w-xl mx-auto mt-8 mb-4" ref={searchRef} data-testid="hero-search">
        <div className="flex">
          <div className="flex items-center px-3 border border-r-0 rounded-l-md" style={{ borderColor: "var(--paper-border)", background: "var(--paper-soft)" }}>
            <span className="font-mono text-[11px]" style={{ color: "var(--paper-muted)" }}>$</span>
          </div>
          <div className="flex items-center flex-1 border border-r-0 px-2" style={{ borderColor: "var(--paper-border)" }}>
            <span className="font-mono text-[11px] shrink-0" style={{ color: "var(--paper-muted)" }}>screen</span>
            <input
              type="text" value={searchVal}
              onChange={e => handleChange(e.target.value)}
              onFocus={handleFocus}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder="--symbol=AAPL"
              className="flex-1 font-mono text-[11px] bg-transparent outline-none px-1 py-2.5 min-w-0"
              style={{ color: "var(--paper-fg)" }}
              data-testid="hero-search-input"
            />
          </div>
          <button onClick={handleSearch} className="font-mono text-[11px] font-bold px-4 py-2.5 rounded-r-md border" style={{ background: "var(--paper-fg)", color: "var(--paper-bg)", borderColor: "var(--paper-fg)" }} data-testid="hero-cta-primary">
            &gt; RUN
          </button>
        </div>

        {/* Auto-suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 border rounded-md shadow-lg z-50 overflow-hidden" style={{ borderColor: "var(--paper-border)", background: "var(--paper-bg)" }} data-testid="search-suggestions">
            {suggestions.map(s => {
              const vColor = s.verdict === "compliant" ? GREEN : s.verdict === "borderline" ? "#f59e0b" : s.verdict === "non_compliant" ? RED : "#a1a1aa";
              const vLabel = s.verdict === "compliant" ? "HALAL" : s.verdict === "borderline" ? "REVIEW" : s.verdict === "non_compliant" ? "NOT HALAL" : "N/A";
              return (
                <button key={s.ticker} onClick={() => pickSuggestion(s.ticker)} className="flex items-center justify-between w-full px-4 py-2.5 hover:bg-[var(--paper-soft)] transition-colors border-b last:border-b-0" style={{ borderColor: "var(--paper-soft)" }} data-testid={`suggestion-${s.ticker}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[12px] font-bold" style={{ color: "var(--paper-fg)" }}>{s.ticker}</span>
                    <span className="text-[11px] truncate max-w-[200px]" style={{ color: "var(--paper-muted)" }}>{s.company_name}</span>
                  </div>
                  <span className="font-mono text-[10px] font-bold" style={{ color: vColor }}>[ {vLabel} ]</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-6 font-mono text-[10px] mt-2 mb-2" style={{ color: "var(--paper-muted)" }}>
        <span>UNIVERSE <span className="font-bold" style={{ color: "var(--paper-fg)" }}>{stats.total_stocks || 115}</span></span>
        <span>SECTORS <span className="font-bold" style={{ color: "var(--paper-fg)" }}>{stats.sectors || 18}</span></span>
        <span className="hidden sm:inline">LATENCY <span className="font-bold" style={{ color: "var(--paper-fg)" }}>114ms</span></span>
        <span>UPDATED <span className="font-bold" style={{ color: "var(--paper-fg)" }}>2m ago</span></span>
      </div>
      </div>
    </section>
  );
}

/* ─── Stats Bar ─── */
/* ─── Tiny sparkline SVG (traditional finance colors) ─── */
function MetricSparkline({ trend = "up", color = "#22c55e", width = 96, height = 26, variant = "line" }) {
  const d = useMemo(() => {
    const seed = trend === "up" ? 0.37 : trend === "down" ? 0.81 : 0.55;
    const pts = Array.from({ length: 22 }, (_, i) => {
      const base = trend === "up" ? 4 + i * 0.55 : trend === "down" ? 16 - i * 0.45 : 10 + Math.sin(i * 0.6) * 1.2;
      const noise = Math.sin((i + 1) * seed * 12.9898) * 1.6;
      return base + Math.sin(i * 0.9) * 1.4 + noise;
    });
    const mn = Math.min(...pts), mx = Math.max(...pts);
    return {
      path: pts.map((p, i) => `${i === 0 ? "M" : "L"}${(i / 21) * (width - 2) + 1},${height - 2 - ((p - mn) / (mx - mn || 1)) * (height - 4)}`).join(" "),
      pts,
      mn, mx,
    };
  }, [trend, width, height]);
  const gradId = `spark-${color.replace("#", "")}-${trend}`;
  if (variant === "bars") {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
        {d.pts.map((p, i) => {
          const barH = ((p - d.mn) / (d.mx - d.mn || 1)) * (height - 4) + 2;
          const barW = (width - 2) / d.pts.length - 1.2;
          return (
            <rect key={i} x={(i / d.pts.length) * (width - 2) + 1} y={height - barH} width={barW} height={barH} fill={color} opacity="0.85" rx="0.6" />
          );
        })}
      </svg>
    );
  }
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d.path} L ${width - 1},${height} L 1,${height} Z`} fill={`url(#${gradId})`} />
      <path d={d.path} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── Count-up animated number (respects prefers-reduced-motion) ─── */
function CountUp({ to, decimals = 0, prefix = "", suffix = "", duration = 1.5 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setVal(to); return; }
    const controls = animate(0, to, {
      duration,
      ease: [0.16, 1, 0.3, 1], // smooth premium easing (same curve as section animations)
      onUpdate: (latest) => setVal(latest),
    });
    return () => controls.stop();
  }, [inView, to, duration]);

  const formatted = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString();
  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
}

function StatsBar({ stats }) {
  const s = stats;
  const total = s.total_stocks || 189;
  const compliant = s.compliant ?? 95;
  const borderline = s.borderline ?? 13;
  const nonCompliant = s.non_compliant ?? 79;
  const passRate = s.pass_rate ?? 50.3;
  const pct = (n) => total > 0 ? ((n / total) * 100) : 0;

  // 5 metric cards driven by real API stats (colors match reference image)
  const cols = [
    {
      label: "Total Screened",
      to: total, decimals: 0, suffix: "",
      sub: "companies",
      badge: null,
      color: "#3b82f6",       // blue
      spark: "up", variant: "line",
    },
    {
      label: "Compliant",
      to: compliant, decimals: 0, suffix: "",
      sub: null,
      badge: { text: `${pct(compliant).toFixed(1)}%`, color: "#22c55e" },
      color: "#22c55e",       // green
      spark: "up", variant: "line",
    },
    {
      label: "Borderline",
      to: borderline, decimals: 0, suffix: "",
      sub: null,
      badge: { text: `${pct(borderline).toFixed(1)}%`, color: "#eab308" },
      color: "#eab308",       // yellow
      spark: "flat", variant: "line",
    },
    {
      label: "Non-Compliant",
      to: nonCompliant, decimals: 0, suffix: "",
      sub: null,
      badge: { text: `${pct(nonCompliant).toFixed(1)}%`, color: "#ef4444" },
      color: "#ef4444",       // red
      spark: "down", variant: "line",
    },
    {
      label: "Pass Rate",
      to: passRate, decimals: 1, suffix: "%",
      sub: "overall",
      badge: null,
      color: "#3b82f6",       // blue
      spark: "up", variant: "bars",
    },
  ];

  return (
    <section id="stats-section" className="px-5 sm:px-10 py-16 border-t border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid="stats-bar">
      <div className="max-w-[1200px] mx-auto">
        {/* Section header */}
        <div className="text-center mb-10">
          <motion.h2
            initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="text-2xl sm:text-3xl font-semibold tracking-[-0.02em] mb-2"
            style={{ fontFamily: HEADLINE_FONT, color: "var(--paper-fg)" }}
          >
            Key Metrics
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.08 }}
            className="text-[13px]"
            style={{ color: "rgba(255,255,255,0.50)", fontFamily: BODY_FONT }}
          >
            Live overview of our screening universe
          </motion.p>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {cols.map((c, idx) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay: idx * 0.07, ease: [0.16, 1, 0.3, 1] }}
              className="group relative rounded-xl px-4 py-4 transition-all duration-300 border"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.008) 100%)",
                borderColor: "rgba(255,255,255,0.07)",
                boxShadow: "0 4px 18px rgba(0,0,0,0.25)",
              }}
              data-testid={`metric-${c.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
            >
              {/* Hover glow */}
              <div
                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: `radial-gradient(circle at 50% 0%, ${c.color}18 0%, transparent 70%)` }}
              />

              <div className="relative flex flex-col">
                {/* Label */}
                <p
                  className="text-[11px] mb-2"
                  style={{ color: "rgba(255,255,255,0.55)", fontFamily: BODY_FONT }}
                >
                  {c.label}
                </p>

                {/* Value + inline badge (Compliant/Borderline/Non-Compliant have percentage badge) */}
                <div className="flex items-baseline gap-2 mb-1">
                  <p
                    className="font-semibold tabular-nums transition-colors duration-300"
                    style={{
                      color: "#ffffff",
                      fontFamily: HEADLINE_FONT,
                      fontSize: "clamp(24px, 2.4vw, 30px)",
                      lineHeight: "1.05",
                      letterSpacing: "-0.03em",
                      fontFeatureSettings: '"tnum" 1, "lnum" 1',
                    }}
                  >
                    <CountUp to={c.to} decimals={c.decimals} suffix={c.suffix} duration={1.4 + idx * 0.05} />
                  </p>
                  {c.badge && (
                    <span className="text-[12px] font-semibold tabular-nums" style={{ color: c.badge.color, fontFamily: BODY_FONT }}>
                      {c.badge.text}
                    </span>
                  )}
                </div>

                {/* Sub-label — always rendered as fixed-height row for consistent vertical rhythm */}
                <p
                  className="text-[10.5px]"
                  style={{ color: "rgba(255,255,255,0.42)", fontFamily: BODY_FONT, minHeight: "14px" }}
                >
                  {c.sub || "\u00A0"}
                </p>

                {/* Sparkline / bar chart — consistent 12px top gap and 4px left indent */}
                <div className="mt-3 pl-1 h-[26px] flex items-end">
                  <MetricSparkline trend={c.spark} color={c.color} height={26} variant={c.variant} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Screener Table (Fix 3: correct status labels, Fix 9: clickable rows) ─── */
/* ─── Company logo — local SVG assets in /public/logos.
 * Every ticker in the feed maps directly to its authentic brand logo.
 * Wordmark logos (Visa, JPMorgan, J&J) render on a wider aspect ratio.
 * Fallback: neutral building icon (never ticker initials).
 * ─── */
const COMPANY_LOGOS = {
  AAPL:  { src: "/logos/apple.svg",           wide: false },
  MSFT:  { src: "/logos/microsoft.svg",       wide: false },
  TSLA:  { src: "/logos/tesla.svg",           wide: false },
  JNJ:   { src: "/logos/jnj.svg",             wide: true  },
  NVDA:  { src: "/logos/nvidia.svg",          wide: false },
  GOOGL: { src: "/logos/google.svg",          wide: false },
  META:  { src: "/logos/meta.svg",            wide: false },
  JPM:   { src: "/logos/jpmorgan.svg",        wide: true  },
  V:     { src: "/logos/visa.svg",            wide: true  },
  BAC:   { src: "/logos/bank-of-america.svg", wide: false },
};

function CompanyLogo({ ticker }) {
  const cfg = COMPANY_LOGOS[ticker];
  const [failed, setFailed] = useState(!cfg);

  if (failed || !cfg) {
    // Neutral building fallback (no ticker initials, per spec)
    return (
      <div
        className="w-[38px] h-[38px] rounded-[9px] flex items-center justify-center shrink-0"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
          <path d="M6 12H4a2 2 0 0 0-2 2v8h4"/>
          <path d="M18 9h2a2 2 0 0 1 2 2v11h-4"/>
          <path d="M10 6h4"/>
          <path d="M10 10h4"/>
          <path d="M10 14h4"/>
          <path d="M10 18h4"/>
        </svg>
      </div>
    );
  }

  return (
    <div
      className="w-[38px] h-[38px] rounded-[9px] flex items-center justify-center overflow-hidden shrink-0"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <img
        src={cfg.src}
        alt={`${ticker} logo`}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{
          width: cfg.wide ? "28px" : "24px",
          height: cfg.wide ? "20px" : "24px",
          objectFit: "contain",
          display: "block",
        }}
      />
    </div>
  );
}

/* ─── Compute AAOIFI score 0–100 from real API ratios.
 * Correlates directly with verdict: compliant 82-99, borderline 60-78, non-compliant 5-45.
 * Uses actual debt/interest/receivable ratios from FMP data.
 * ─── */
function computeAaoifiScore(s) {
  if (!s || s.verdict === "unverified") return null;
  const debt = s.debt_ratio ?? 100;
  const interest = s.interest_ratio ?? 100;
  const recv = s.receivable_ratio ?? 100;

  // Headroom = (threshold − actual) / threshold. Positive = passing (below threshold).
  const debtHead = (33 - debt) / 33;
  const intHead  = (5 - interest) / 5;
  const recvHead = (49 - recv) / 49;

  if (s.verdict === "non_compliant") {
    const worstFail = Math.min(debtHead, intHead, recvHead); // most-negative if any ratio failed
    if (worstFail >= 0) {
      // All ratios pass but the sector / business activity is excluded (e.g. banks, insurance,
      // payment networks like VISA). Return a fixed low score so it's clearly non-halal.
      return 22;
    }
    const bounded = Math.max(-2, Math.min(0, worstFail));    // clamp -2..0
    return Math.round(45 + bounded * 20);
  }
  if (s.verdict === "borderline") {
    // All ratios technically pass but one is close to threshold. Range 60–78.
    const avgHead = (debtHead + intHead + recvHead) / 3;
    const bounded = Math.max(0, Math.min(0.4, avgHead));
    return Math.round(60 + bounded * 45);
  }
  // Compliant: 82–99 based on average headroom below thresholds.
  const avgHead = (debtHead + intHead + recvHead) / 3;
  const bounded = Math.max(0.05, Math.min(1, avgHead));
  return Math.round(82 + bounded * 17);
}

function timeAgo(iso) {
  if (!iso) return "recently";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  return `${months} mo${months === 1 ? "" : "s"} ago`;
}

function ScreenerTable({ onEnterScreener }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");
  useEffect(() => {
    const fetchData = async () => {
      try {
        const tickers = ["AAPL","MSFT","TSLA","JNJ","NVDA","GOOGL","META","JPM","V","BAC"];
        const sr = await axios.get(`${API}/stocks`);
        const sm = {}; (sr.data || []).forEach(s => { sm[s.ticker] = s; });
        setRows(tickers.map(t => {
          const s = sm[t] || {};
          // Traditional finance palette (matches reference image)
          const vMap = {
            compliant:     { l: "Compliant",     bg: "rgba(34,197,94,0.14)",  fg: "#4ade80", accent: "#22c55e" },
            borderline:    { l: "Borderline",    bg: "rgba(245,158,11,0.14)", fg: "#fbbf24", accent: "#f59e0b" },
            non_compliant: { l: "Non-Compliant", bg: "rgba(239,68,68,0.14)",  fg: "#f87171", accent: "#ef4444" },
            unverified:    { l: "Unverified",    bg: "rgba(161,161,170,0.10)",fg: "#a1a1aa", accent: "#71717a" },
          };
          const v = vMap[s.verdict] || vMap.unverified;
          return {
            t,
            n: s.company_name || t,
            vl: v.l,
            vBg: v.bg, vFg: v.fg, vAccent: v.accent,
            verdict: s.verdict || "unverified",
            score: computeAaoifiScore(s),
            updated: s.last_updated,
          };
        }));
      } catch { /* */ }
    };
    fetchData();
  }, []);

  const filtered = filter === "ALL" ? rows : rows.filter(r => {
    if (filter === "COMPLIANT") return r.verdict === "compliant";
    if (filter === "BORDERLINE") return r.verdict === "borderline";
    if (filter === "NON-COMPLIANT") return r.verdict === "non_compliant";
    return true;
  });

  return (
    <section id="features" className="px-5 sm:px-10 py-14 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid="screener-table-section">
      <div className="max-w-[1200px] mx-auto">
        {/* Section header */}
        <div className="flex items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold tracking-[-0.02em] mb-1" style={{ color: "var(--paper-fg)", fontFamily: HEADLINE_FONT }}>
              Recently Updated Screenings
            </h2>
            <p className="text-[12.5px]" style={{ color: "rgba(255,255,255,0.50)", fontFamily: BODY_FONT }}>
              Latest AAOIFI compliance updates
            </p>
          </div>
          <button
            onClick={() => onEnterScreener()}
            className="text-[11.5px] font-medium px-3.5 py-1.5 rounded-md border transition-colors hover:bg-white/[0.04]"
            style={{ color: "rgba(255,255,255,0.85)", borderColor: "rgba(255,255,255,0.10)", fontFamily: BODY_FONT }}
            data-testid="feed-view-all-btn"
          >
            View all
          </button>
        </div>

        {/* Feed rows */}
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: "rgba(255,255,255,0.07)", background: "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.005) 100%)" }}>
          <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
            {filtered.map((r) => (
              <li
                key={r.t}
                className="group flex items-center gap-4 px-5 py-3.5 cursor-pointer transition-colors hover:bg-white/[0.02]"
                onClick={() => onEnterScreener(null, r.t)}
                data-testid={`row-${r.t}`}
              >
                {/* Logo */}
                <CompanyLogo ticker={r.t} />

                {/* Ticker */}
                <p
                  className="text-[13px] font-semibold w-16 shrink-0 tabular-nums"
                  style={{ color: "var(--paper-fg)", fontFamily: HEADLINE_FONT, letterSpacing: "-0.01em" }}
                >
                  {r.t}
                </p>

                {/* Company name */}
                <p
                  className="text-[13px] flex-1 min-w-0 truncate"
                  style={{ color: "rgba(255,255,255,0.68)", fontFamily: BODY_FONT }}
                >
                  {r.n}
                </p>

                {/* Compliance pill */}
                <span
                  className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold shrink-0"
                  style={{ background: r.vBg, color: r.vFg, fontFamily: BODY_FONT }}
                >
                  {r.vl}
                </span>

                {/* AAOIFI score */}
                <p
                  className="text-[12.5px] font-medium tabular-nums w-[68px] text-right shrink-0"
                  style={{ color: "rgba(255,255,255,0.85)", fontFamily: BODY_FONT }}
                >
                  {r.score != null ? (
                    <>
                      {r.score}
                      <span style={{ color: "rgba(255,255,255,0.40)" }}> / 100</span>
                    </>
                  ) : (
                    <span style={{ color: "rgba(255,255,255,0.35)" }}>—</span>
                  )}
                </p>

                {/* Updated timestamp */}
                <p
                  className="text-[11.5px] tabular-nums w-[70px] text-right shrink-0"
                  style={{ color: "rgba(255,255,255,0.45)", fontFamily: BODY_FONT }}
                >
                  {timeAgo(r.updated)}
                </p>

                {/* Kebab (visual only) */}
                <span className="text-[16px] leading-none shrink-0" style={{ color: "rgba(255,255,255,0.30)" }}>⋯</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ─── Methodology — alternating vertical timeline ─── */
function TimelineNode({ index, hovered }) {
  const isHot = hovered === index;
  return (
    <div className="relative">
      {/* outer halo (always present, brightens on hover) */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{
          boxShadow: isHot
            ? "0 0 0 8px rgba(14,165,233,0.08), 0 0 24px rgba(14,165,233,0.55)"
            : "0 0 0 6px rgba(14,165,233,0.04), 0 0 14px rgba(14,165,233,0.30)",
        }}
        transition={{ duration: 0.3 }}
      />
      <motion.div
        className="relative w-4 h-4 rounded-full border"
        style={{
          background: isHot
            ? "linear-gradient(135deg, #38bdf8, #0ea5e9)"
            : "linear-gradient(135deg, #0c4a6e, #075985)",
          borderColor: isHot ? "rgba(56,189,248,0.85)" : "rgba(56,189,248,0.45)",
        }}
        animate={{ scale: isHot ? 1.15 : 1 }}
        transition={{ duration: 0.3 }}
      />
    </div>
  );
}

function MethodologyCard({ step, side, hovered, setHovered, index }) {
  const isLeft = side === "left";
  return (
    <motion.div
      initial={{ opacity: 0, y: 24, x: isLeft ? -24 : 24 }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(index)}
      onMouseLeave={() => setHovered(null)}
      className="group relative rounded-2xl border p-6 transition-all cursor-default"
      style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)",
        borderColor: hovered === index ? "rgba(56,189,248,0.35)" : "rgba(255,255,255,0.08)",
        backdropFilter: "blur(12px)",
        boxShadow: hovered === index
          ? "0 12px 40px rgba(14,165,233,0.12), inset 0 1px 0 rgba(255,255,255,0.05)"
          : "0 4px 20px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      <div className="flex items-baseline gap-3 mb-3">
        <span
          className="text-[28px] font-light tabular-nums leading-none"
          style={{
            background: "linear-gradient(135deg, #38bdf8, #0ea5e9)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontFamily: HEADLINE_FONT,
            letterSpacing: "-0.04em",
          }}
        >
          {step.num}
        </span>
        <span className="h-px flex-1" style={{ background: "linear-gradient(90deg, rgba(56,189,248,0.30), transparent)" }} />
      </div>
      <h3 className="text-[16px] font-semibold tracking-tight mb-2" style={{ color: "var(--paper-fg)", fontFamily: HEADLINE_FONT }}>
        {step.title}
      </h3>
      <p className="text-[13px] leading-relaxed" style={{ color: "var(--paper-muted)", fontFamily: BODY_FONT }}>
        {step.desc}
      </p>
    </motion.div>
  );
}

function Methodology() {
  const steps = [
    { num: "01", title: "Sector Filter", desc: "Exclude conventional finance, alcohol, gambling, weapons, adult entertainment." },
    { num: "02", title: "Compute Ratios", desc: "Debt/MCap < 33%. Interest/Revenue < 5%. Receivables/Assets < 45%." },
    { num: "03", title: "Cite Sources", desc: "Every figure links directly to its SEC filing source. Cached, timestamped, fully verifiable." },
    { num: "04", title: "Recheck Quarterly", desc: "Re-screen on new filings. Intraday flags when material disclosures change the math." },
  ];

  const [hovered, setHovered] = useState(null);
  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: timelineRef, offset: ["start 70%", "end 30%"] });
  const fillHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="methodology" className="px-5 sm:px-10 py-24 border-t" style={{ borderColor: "var(--paper-border)" }} data-testid="methodology-section">
      <div className="max-w-[1200px] mx-auto">
        {/* Header — full width */}
        <div className="text-center mb-20">
          <motion.p
            initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="text-[10px] uppercase tracking-[0.20em] font-medium mb-5"
            style={{ color: "rgba(56,189,248,0.85)", fontFamily: BODY_FONT }}
          >
            Methodology
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.05 }}
            className="text-4xl sm:text-5xl lg:text-[52px] font-semibold leading-[1.05] tracking-[-0.025em] mb-5"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            <span style={{ color: "var(--paper-fg)" }}>The math </span>
            <span className="hero-gradient-text">shows its work.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-[15px] leading-relaxed max-w-xl mx-auto"
            style={{ color: "var(--paper-muted)", fontFamily: BODY_FONT }}
          >
            Four steps. No black boxes. Reviewed by an independent Sharia board, audited against AAOIFI.
          </motion.p>
        </div>

        {/* Timeline */}
        <div ref={timelineRef} className="relative max-w-[920px] mx-auto">
          {/* Desktop center line */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px" style={{ background: "rgba(255,255,255,0.08)" }} />
          {/* Desktop progressive fill */}
          <motion.div
            className="hidden md:block absolute left-1/2 top-0 -translate-x-1/2 w-px origin-top"
            style={{
              height: fillHeight,
              background: "linear-gradient(180deg, #38bdf8 0%, #0ea5e9 60%, rgba(14,165,233,0.0) 100%)",
              boxShadow: "0 0 8px rgba(56,189,248,0.5)",
            }}
          />
          {/* Mobile line (left) */}
          <div className="md:hidden absolute left-[15px] top-0 bottom-0 w-px" style={{ background: "rgba(255,255,255,0.08)" }} />
          <motion.div
            className="md:hidden absolute left-[15px] top-0 w-px origin-top"
            style={{
              height: fillHeight,
              background: "linear-gradient(180deg, #38bdf8 0%, #0ea5e9 100%)",
              boxShadow: "0 0 8px rgba(56,189,248,0.5)",
            }}
          />

          <div className="flex flex-col gap-12 md:gap-16">
            {steps.map((step, idx) => {
              const side = idx % 2 === 0 ? "left" : "right";
              return (
                <div key={step.num} className="relative">
                  {/* Desktop layout */}
                  <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center gap-0">
                    {side === "left" ? (
                      <>
                        <div className="pr-10">
                          <MethodologyCard step={step} side="left" hovered={hovered} setHovered={setHovered} index={idx} />
                        </div>
                        <div className="flex items-center justify-center w-10">
                          <TimelineNode index={idx} hovered={hovered} />
                        </div>
                        <div />
                      </>
                    ) : (
                      <>
                        <div />
                        <div className="flex items-center justify-center w-10">
                          <TimelineNode index={idx} hovered={hovered} />
                        </div>
                        <div className="pl-10">
                          <MethodologyCard step={step} side="right" hovered={hovered} setHovered={setHovered} index={idx} />
                        </div>
                      </>
                    )}
                  </div>
                  {/* Mobile layout */}
                  <div className="md:hidden flex gap-5 items-start">
                    <div className="flex items-center justify-center w-[31px] shrink-0 pt-6">
                      <TimelineNode index={idx} hovered={hovered} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <MethodologyCard step={step} side="left" hovered={hovered} setHovered={setHovered} index={idx} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Bottom Hadith Section (Fix 8: replaces CTA) ─── */
function HadithSection({ onEnterScreener }) {
  return (
    <section className="text-center px-5 sm:px-10 py-20 border-t" style={{ borderColor: "var(--paper-border)" }} data-testid="final-cta-section">
      <div className="max-w-2xl mx-auto">
        <p className="text-2xl sm:text-3xl leading-[1.35] font-light tracking-tight mb-5" style={{ fontFamily: HEADLINE_FONT, color: "var(--paper-fg)", letterSpacing: "-0.015em" }}>
          &ldquo;A time will come upon the people when one will not care how one gains one&rsquo;s money, lawfully or unlawfully.&rdquo;
        </p>
        <p className="text-[11px] tracking-[0.08em] uppercase mb-10" style={{ color: "var(--paper-muted)", fontFamily: BODY_FONT }}>
          Prophet Muhammad ﷺ &nbsp;·&nbsp; Sahih al-Bukhari, 2059
        </p>
        <p className="text-sm mb-8 max-w-md mx-auto leading-relaxed" style={{ color: "var(--paper-muted)", fontFamily: BODY_FONT }}>
          We built this tool so you never have to wonder. Every stock, every ratio, every verdict — transparent and verifiable.
        </p>
        <button
          onClick={() => onEnterScreener()}
          className="text-[12px] font-semibold tracking-wide px-7 py-3 rounded-md transition-transform hover:scale-[1.02]"
          style={{ background: "var(--paper-fg)", color: "var(--paper-bg)", fontFamily: BODY_FONT }}
          data-testid="final-cta-btn"
        >
          GET STARTED →
        </button>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function Footer({ onNavigate }) {
  return (
    <footer className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 sm:px-10 py-5 border-t" style={{ borderColor: "var(--paper-border)" }} data-testid="landing-footer">
      <span className="text-[10px] tracking-wide" style={{ color: "var(--paper-muted)", fontFamily: BODY_FONT }}>HALAL SCREENER · FMP DATA · AAOIFI STANDARDS · © 2026</span>
      <button onClick={() => onNavigate("privacy")} className="text-[10px] hover:underline" style={{ color: "var(--paper-muted)", fontFamily: BODY_FONT }}>Privacy</button>
    </footer>
  );
}

/* ─── Main ─── */
export default function LandingPage({ onEnterScreener, onNavigate, onStocksFetched }) {
  const [stats, setStats] = useState({});
  const [allStocks, setAllStocks] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/stocks`).then(r => { if (r.data?.length) { onStocksFetched?.(r.data); setAllStocks(r.data); } }).catch(() => {}),
      axios.get(`${API}/stocks/stats`).then(r => { if (r.data) setStats(r.data); }).catch(() => {}),
    ]);
  }, [onStocksFetched]);

  // Fixed dark palette — theme toggle removed
  const palette = { "--paper-bg": "#0a0a0a", "--paper-fg": "#fafafa", "--paper-muted": "#a1a1aa", "--paper-border": "#27272a", "--paper-soft": "#18181b" };

  return (
    <div className="min-h-screen" style={{ ...palette, background: "var(--paper-bg)", color: "var(--paper-fg)", fontFamily: BODY_FONT }}>
      <Navbar onEnterScreener={onEnterScreener} />
      <TickerBar />
      <Hero onEnterScreener={onEnterScreener} stats={stats} allStocks={allStocks} />
      <StatsBar stats={stats} />
      <ScreenerTable onEnterScreener={onEnterScreener} />
      <Methodology />
      <HadithSection onEnterScreener={onEnterScreener} />
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
